import { JOURNEY_TYPE, TRAIN_PLACE_TYPE } from '~constants/pointToPointTicket';

import type {
  PointToPointTicketListResponse,
  SearchPointToPointTicketParam,
  SearchPointToPointTicketData,
  SearchPlaceInformation,
} from '~models/Transportation/PointToPointTicket';

import type { PointToPointTicketFormProps } from '~types/pointToPointTicket';
import type { JourneyType } from '~types/queryParam';

type TrainPlaceType = (typeof TRAIN_PLACE_TYPE)[keyof typeof TRAIN_PLACE_TYPE];

interface MapToSearchRequestParams {
  journeyType: string;
  origin: { code: string; type: string; label: string };
  destination: { code: string; type: string; label: string };
  departureDate: string;
  arrivalDate?: string;
  ages?: number[];
  outboundDepartureTime?: string; // time "09:00"
  inboundDepartureTime?: string; // time "15:00"
}
/**
 * 검색 API 요청을 위한 파라미터 매핑 함수
 */
export const mapToSearchRequest = (
  params: MapToSearchRequestParams,
): SearchPointToPointTicketParam => ({
  type: params.journeyType as JourneyType,
  origin: {
    code: params.origin.code,
    type: params.origin.type as TrainPlaceType,
    label: params.origin.label,
  },
  destination: {
    code: params.destination.code,
    type: params.destination.type as TrainPlaceType,
    label: params.destination.label,
  },
  departure: params.departureDate,
  ...(params.arrivalDate && { arrival: params.arrivalDate }),
  ages: params.ages || [],
  ...(params.outboundDepartureTime && { outboundDepartureTime: params.outboundDepartureTime }),
  ...(params.inboundDepartureTime && { inboundDepartureTime: params.inboundDepartureTime }),
});

/**
 * 검색 조건 로컬 스토리지 저장을 위한 데이터 매핑 함수
 */
export const mapToStorageData = (
  result: Pick<SearchPointToPointTicketData, 'correlationId' | 'id' | 'request'>,
): Partial<
  Omit<PointToPointTicketFormProps, 'origin' | 'destination'> & {
    journey: JourneyType;
    correlationId: string;
    searchId: string;
    origin: SearchPlaceInformation;
    destination: SearchPlaceInformation;
  }
> => ({
  journey: result.request.type,
  origin: {
    type: result.request.origin.type,
    code: result.request.origin.code,
    label: result.request.origin.label,
  },
  destination: {
    type: result.request.destination.type,
    code: result.request.destination.code,
    label: result.request.destination.label,
  },
  departureDate: result.request.departure,
  ...(result.request.type === JOURNEY_TYPE.ROUND_TRIP && {
    arrivalDate: result.request.arrival,
  }),
  ...(result.request.outboundDepartureTime && {
    outboundDepartureTime: result.request.outboundDepartureTime.replace(/:00:00/, ':00'),
  }),
  ...(result.request.inboundDepartureTime && {
    inboundDepartureTime: result.request.inboundDepartureTime.replace(/:00:00/, ':00'),
  }),
  ages: result.request.ages,
  correlationId: result.correlationId,
  searchId: result.id,
});

/**
 * 검색 결과 페이지 이동을 위한 파라미터 매핑 함수
 */
export const mapToNavigationParams = (
  result: SearchPointToPointTicketData,
): {
  correlationId: string;
  journey: JourneyType;
  origin: SearchPlaceInformation;
  destination: SearchPlaceInformation;
  departureDate: string;
  arrivalDate?: string;
  ages: string;
  outboundDepartureTime?: string;
  inboundDepartureTime?: string;
  outboundOfferId?: string;
} => ({
  correlationId: result.correlationId,
  journey: result.request.type,
  origin: {
    code: result.request.origin.code,
    type: result.request.origin.type,
    label: result.request.origin.label,
  },
  destination: {
    code: result.request.destination.code,
    type: result.request.destination.type,
    label: result.request.destination.label,
  },
  departureDate: result.request.departure,
  arrivalDate: result.request.arrival,
  ...(result.request.outboundDepartureTime && {
    outboundDepartureTime: result.request.outboundDepartureTime.replace(/:00:00/, ':00'),
  }),
  ...(result.request.inboundDepartureTime && {
    inboundDepartureTime: result.request.inboundDepartureTime.replace(/:00:00/, ':00'),
  }),
  ages: result.request.ages.join(','),
});

export const researchOutbound = async ({
  searchParams,
  searchOutbound,
  onSuccessResearch,
  onFailResearch,
}: {
  searchParams: MapToSearchRequestParams;
  searchOutbound: (
    searchParams: SearchPointToPointTicketParam,
  ) => Promise<PointToPointTicketListResponse>;
  onSuccessResearch: (result: SearchPointToPointTicketData) => void;
  onFailResearch?: (error: unknown) => void;
}) => {
  const searchRequestParams = mapToSearchRequest(searchParams);
  try {
    const { data: result } = await searchOutbound(searchRequestParams);
    onSuccessResearch(result);
  } catch (error) {
    onFailResearch?.(error);
  }
};
